import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from './redux/store'
import { StepperProvider } from './context/stepper-provider'
import NetworkStatus from './components/NetworkStatus'
import ServiceWorkerRegistration from './components/ServiceWorkerRegistration'
import SessionTimeout from './components/SessionTimeout'
import CacheCleaner from './components/CacheCleaner'
import { ProtectedRoute } from './components/ProtectedRoute'
import Login from './pages/Login'
import Offline from './pages/offline/Offline'
import OfflineProjects from './pages/offline/Projects'
import NewOfflineProjectPage from './pages/offline/projects/new-project'
import EditOfflineProjectPage from './pages/offline/projects/edit-project/[id]'
import { getSerwist } from "virtual:serwist";
// Import online routes
import Projects from './pages/projects'
import NewProject from './pages/projects/new-project'
import EditProject from './pages/projects/edit-project'
import DistributionNewProjectLayout from './pages/distribution-analytics/new-project/DistributionLayout'
import DistributionEditProjectLayout from './pages/distribution-analytics/edit-project/DistributionLayout'
import Home from './pages/Home'
import Navbar from './components/top-nav'
import OktaCallback from './pages/OktaCallback'
import { MockModeControls } from './components/MockModeControls'
// Placeholder for the main app content
// const Home = () => (
//   <div className="p-8">
//     <h1 className="text-3xl font-bold mb-4">DCS Application</h1>
//     <p className="mb-4">Welcome to the DCS application.</p>
//     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//       <div className="p-4 border rounded shadow">
//         <h2 className="text-xl font-semibold mb-2">Projects</h2>
//         <p>Manage your projects here.</p>
//       </div>
//       <div className="p-4 border rounded shadow">
//         <h2 className="text-xl font-semibold mb-2">Users</h2>
//         <p>Manage users here.</p>
//       </div>
//     </div>
//   </div>
// )

export default function App() {
  const isOfflineMode = useSelector((state: RootState) => state.offline.isOfflineMode)
  const isOnline = useSelector((state: RootState) => state.netWork.isOnline)
  const navigate = useNavigate()
  const location = useLocation()

  // Handle initial page load when offline
  useEffect(() => {
    // Only redirect if:
    // 1. User is completely offline (browser reports offline)
    // 2. User is on the root path (initial load)
    // 3. Not already on a login or offline page
    const isInitialLoad = location.pathname === '/'
    const isNotOnSpecialPage = !location.pathname.includes('/login') &&
      !location.pathname.includes('/offline')

    if (!isOnline && isInitialLoad && isNotOnSpecialPage) {
      navigate('/login?offlineMode=true')
    }

    // Don't automatically redirect to offline pages when user manually sets offline mode
    // Let them navigate manually or use the navbar button
  }, [isOnline, navigate, location.pathname])

  // Register service worker
  useEffect(() => {
    const registerSW = async () => {
      if ("serviceWorker" in navigator) {
        try {
          const serwist = await getSerwist();

          serwist?.addEventListener("installed", (event) => {
            console.log("Serwist installed!");
            if (event.isUpdate) {
              // New version available
              if (confirm('A new version is available. Reload to update?')) {
                window.location.reload();
              }
            }
          });

          await serwist?.register();
        } catch (error) {
          console.error("Failed to register service worker:", error);
        }
      }
    };

    registerSW();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <ServiceWorkerRegistration />
      <NetworkStatus />
      {!isOfflineMode && <SessionTimeout />}
      <CacheCleaner />
      <MockModeControls />

      <Routes>
        {/* Public routes available in all modes */}
        <Route path="/login" element={<Login />} />
        <Route path="/okta-login" element={<OktaCallback />} />
        <Route path="/login/callback" element={<OktaCallback />} />
        <Route path="/offline" element={<Offline />} />

        {/* Offline routes - always accessible regardless of online/offline status */}
        <Route path="/offline/projects" element={
          <StepperProvider>
            <OfflineProjects />
          </StepperProvider>
        } />
        <Route path="/offline/projects/new-project" element={
          <StepperProvider>
            <NewOfflineProjectPage />
          </StepperProvider>
        } />
        <Route path="/offline/projects/edit-project/:id" element={
          <StepperProvider>
            <EditOfflineProjectPage />
          </StepperProvider>
        } />

        {/* Online routes - only accessible when not in offline mode */}
        {(
          <>
            <Route path="/" element={<ProtectedRoute>
              <Navbar />
              <Home />
            </ProtectedRoute>} />

            <Route path="/projects" element={
              <ProtectedRoute>
                <Projects />
              </ProtectedRoute>
            } />
            <Route path="/projects/new-project" element={
              <ProtectedRoute>
                <StepperProvider>
                  <NewProject />
                </StepperProvider>
              </ProtectedRoute>
            } />
            <Route path="/projects/edit-project/:id" element={
              <ProtectedRoute>
                <StepperProvider>
                  <EditProject />
                </StepperProvider>
              </ProtectedRoute>
            } />

            <Route path="/distribution-analytics/new-project" element={
              <ProtectedRoute>
                <DistributionNewProjectLayout />
              </ProtectedRoute>
            } />
            <Route path="/distribution-analytics/view-project/:id" element={
              <ProtectedRoute>
                <DistributionEditProjectLayout />
              </ProtectedRoute>
            } />

            
          </>
        )}

        {/* Fallback routes */}
        <Route path="*" element={isOfflineMode
          ? <Navigate to="/offline/projects" replace />
          : <Navigate to="/" replace />
        } />
      </Routes>
    </div>
  )
}
